import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Hook to synchronize filters and pagination with URL query parameters
 */
const useUrlSync = (filters, pagination, onFiltersChange, onPaginationChange) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL on mount
  useEffect(() => {
    const urlFilters = {};
    const urlPagination = {};

    // Parse search text
    if (searchParams.get('search')) {
      urlFilters.searchText = searchParams.get('search');
    }

    // Parse date range
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    if (startDate && endDate) {
      urlFilters.selectedDateRange = [dayjs(startDate), dayjs(endDate)];
    }

    // Parse other filters - filter out empty strings from URL params like store=&financialStatus=
    const storeIds = searchParams.getAll('store').filter(v => v.trim() !== '');
    if (storeIds.length > 0) {
      urlFilters.selectedStore = storeIds;
    }
    const financialStatuses = searchParams.getAll('financialStatus').filter(v => v.trim() !== '');
    if (financialStatuses.length > 0) {
      urlFilters.selectedFinancialStatus = financialStatuses;
    }
    const fulfillmentStatuses = searchParams.getAll('fulfillmentStatus').filter(v => v.trim() !== '');
    if (fulfillmentStatuses.length > 0) {
      urlFilters.selectedFulfillmentStatus = fulfillmentStatuses;
    }
    const shipmentStatuses = searchParams.getAll('shipmentStatus').filter(v => v.trim() !== '');
    if (shipmentStatuses.length > 0) {
      urlFilters.selectedShipmentStatus = shipmentStatuses.flatMap(v => v.split(',')).map(v => v.trim()).filter(v => v.length > 0);
    }
    if (searchParams.get('shipmentDetail')) {
      urlFilters.selectedShipmentDetail = searchParams.get('shipmentDetail');
      if (urlFilters.selectedShipmentDetail === 'stale_over_3_days' && (!urlFilters.selectedShipmentStatus || urlFilters.selectedShipmentStatus.length === 0)) {
        urlFilters.selectedShipmentStatus = ['in_transit'];
      }
    }
    if (searchParams.get('confirmShipment')) {
      urlFilters.selectedConfirmShipment = searchParams.get('confirmShipment');
    }
    const categoryIds = searchParams.getAll('categoryId').filter(v => v.trim() !== '');
    if (categoryIds.length > 0) {
      urlFilters.selectedCategory = categoryIds;
    }
    const collectionIds = searchParams.getAll('collectionId').filter(v => v.trim() !== '');
    if (collectionIds.length > 0) {
      urlFilters.selectedCollection = collectionIds;
    }
    const printerIds = searchParams.getAll('printerId').filter(v => v.trim() !== '');
    if (printerIds.length > 0) {
      urlFilters.selectedPrinter = printerIds;
    }
    const supporterIds = searchParams.getAll('supporterId').filter(v => v.trim() !== '');
    if (supporterIds.length > 0) {
      urlFilters.selectedSupporter = supporterIds;
    }
    if (searchParams.get('title')) {
      urlFilters.selectedTitle = searchParams.get('title');
    }
    if (searchParams.get('sku')) {
      urlFilters.selectedSku = searchParams.get('sku');
    }
    if (searchParams.get('employeeId')) {
      urlFilters.selectedEmployeeId = searchParams.get('employeeId');
    }
    if (searchParams.get('excludeTitle')) {
      urlFilters.selectedExcludeTitle = searchParams.get('excludeTitle');
    }
    if (searchParams.get('excludeSku')) {
      urlFilters.selectedExcludeSku = searchParams.get('excludeSku');
    }
    if (searchParams.get('basecost')) {
      urlFilters.selectedBasecost = searchParams.get('basecost');
    }

    // Parse pagination
    if (searchParams.get('page')) {
      urlPagination.current = parseInt(searchParams.get('page'));
    }
    if (searchParams.get('pageSize')) {
      urlPagination.pageSize = parseInt(searchParams.get('pageSize'));
    }

    // Apply URL params to state if they exist
    if (Object.keys(urlFilters).length > 0) {
      onFiltersChange(urlFilters);
    }
    if (Object.keys(urlPagination).length > 0) {
      onPaginationChange(urlPagination);
    }
  }, []); // Only run on mount

  // Function to update URL when filters/pagination change
  const updateUrl = useCallback((newFilters, newPagination) => {
    const params = new URLSearchParams();

    // Add filters to URL
    if (newFilters.searchText) {
      params.set('search', newFilters.searchText);
    }
    if (newFilters.selectedDateRange && newFilters.selectedDateRange.length === 2) {
      params.set('startDate', dayjs(newFilters.selectedDateRange[0]).format('YYYY-MM-DD'));
      params.set('endDate', dayjs(newFilters.selectedDateRange[1]).format('YYYY-MM-DD'));
    }
    const appendArrayParam = (key, value) => {
      if (Array.isArray(value) && value.length > 0) {
        value.forEach(v => params.append(key, v));
      } else if (value && value !== 'all') {
        params.set(key, value);
      }
    };

    appendArrayParam('store', newFilters.selectedStore);
    appendArrayParam('financialStatus', newFilters.selectedFinancialStatus);
    appendArrayParam('fulfillmentStatus', newFilters.selectedFulfillmentStatus);
    appendArrayParam('shipmentStatus', newFilters.selectedShipmentStatus);
    if (newFilters.selectedShipmentDetail && newFilters.selectedShipmentDetail !== 'all') {
      params.set('shipmentDetail', newFilters.selectedShipmentDetail);
    }
    if (newFilters.selectedConfirmShipment && newFilters.selectedConfirmShipment !== 'all') {
      params.set('confirmShipment', newFilters.selectedConfirmShipment);
    }
    appendArrayParam('categoryId', newFilters.selectedCategory);
    appendArrayParam('collectionId', newFilters.selectedCollection);
    appendArrayParam('printerId', newFilters.selectedPrinter);
    appendArrayParam('supporterId', newFilters.selectedSupporter);
    if (newFilters.selectedTitle && newFilters.selectedTitle.trim()) {
      params.set('title', newFilters.selectedTitle.trim());
    }
    if (newFilters.selectedSku && newFilters.selectedSku.trim()) {
      params.set('sku', newFilters.selectedSku.trim());
    }
    if (newFilters.selectedEmployeeId && newFilters.selectedEmployeeId.trim()) {
      params.set('employeeId', newFilters.selectedEmployeeId.trim());
    }
    if (newFilters.selectedExcludeTitle && newFilters.selectedExcludeTitle.trim()) {
      params.set('excludeTitle', newFilters.selectedExcludeTitle.trim());
    }
    if (newFilters.selectedExcludeSku && newFilters.selectedExcludeSku.trim()) {
      params.set('excludeSku', newFilters.selectedExcludeSku.trim());
    }
    if (newFilters.selectedBasecost && newFilters.selectedBasecost !== 'all') {
      params.set('basecost', newFilters.selectedBasecost);
    } else {
      params.delete('basecost');
    }

    // Add pagination to URL
    if (newPagination.current) {
      params.set('page', newPagination.current);
    }
    if (newPagination.pageSize) {
      params.set('pageSize', newPagination.pageSize);
    }

    setSearchParams(params);
  }, [setSearchParams]);

  return { updateUrl };
};

export default useUrlSync;
